/**
 * Launch intents carried by a push notification's URL (see
 * `PWA_PARAM` in apps/api/src/modules/web-push/web-push-payloads.ts):
 *
 *   pwa_read=<notificationId>     mark that bell notification read
 *   pwa_wa_conv=<conversationId>  open the WhatsApp widget on that chat
 *   pwa_wa_call=<callId>          bring up the answer screen for that call
 *   pwa_wa_answer=1               (with pwa_wa_call) answer it straight away -
 *                                 the alert's "Answer" button
 *
 * They are lifted off the URL as early as possible (main.tsx, before the
 * router mounts) and parked in sessionStorage, because a signed-out tap goes
 * through /login first and ProtectedRoute only preserves the pathname.
 */

export interface PushLaunch {
  readNotificationId?: string;
  conversationId?: string;
  callId?: string;
  /** Answer the call at once, not just show it ringing. Only with `callId`. */
  answerCall?: boolean;
}

type IdField = Exclude<keyof PushLaunch, 'answerCall'>;

const PARAMS = {
  readNotificationId: 'pwa_read',
  conversationId: 'pwa_wa_conv',
  callId: 'pwa_wa_call',
} as const satisfies Record<IdField, string>;
const ANSWER_PARAM = 'pwa_wa_answer';

const STORAGE_KEY = 'utazzo.push.launch';
/** A parked intent older than this (e.g. abandoned login) is dropped. */
const MAX_AGE_MS = 10 * 60 * 1000;
/** Ids are UUIDs / provider call ids - reject anything that isn't plain. */
const SAFE_ID = /^[A-Za-z0-9._:-]{1,200}$/;
/**
 * WhatsApp call ids (`wacid.HBg...`) are base64, so `+ / =` must pass too: an
 * id dropped here silently turns "tap to answer" into nothing at all. Only the
 * call id gets the wider set (it only feeds a GET of that call's status), and
 * never a `..` - no base64 id has one, and it is how a path would climb.
 */
const CALL_ID = /^[A-Za-z0-9._:+/=-]{1,200}$/;

const isSafe = (field: IdField, value: string): boolean =>
  field === 'callId' ? CALL_ID.test(value) && !value.includes('..') : SAFE_ID.test(value);

const isEmpty = (l: PushLaunch): boolean => !l.readNotificationId && !l.conversationId && !l.callId;

/**
 * Split a URL into the in-app path to navigate to (launch params removed,
 * other params kept) and the launch intent.
 */
export const splitLaunchUrl = (href: string): { path: string; launch: PushLaunch } => {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return { path: '/', launch: {} };
  }
  // Never follow a foreign origin out of a message.
  if (url.origin !== window.location.origin) return { path: '/', launch: {} };

  const launch: PushLaunch = {};
  for (const [field, param] of Object.entries(PARAMS) as Array<[IdField, string]>) {
    const value = url.searchParams.get(param);
    if (value && isSafe(field, value)) launch[field] = value;
    url.searchParams.delete(param);
  }
  const answer = url.searchParams.get(ANSWER_PARAM);
  url.searchParams.delete(ANSWER_PARAM);
  if (answer === '1' && launch.callId) launch.answerCall = true;
  const search = url.searchParams.toString();
  return { path: `${url.pathname}${search ? `?${search}` : ''}${url.hash}`, launch };
};

const park = (launch: PushLaunch): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...launch, at: Date.now() }));
  } catch {
    // Storage blocked - the intent is lost, the page itself still opens.
  }
};

/**
 * main.tsx, before render: move launch params off the address bar into
 * sessionStorage. Leaves the URL untouched when there are none.
 */
export const capturePushLaunchFromLocation = (): void => {
  if (typeof window === 'undefined') return;
  const { path, launch } = splitLaunchUrl(window.location.href);
  if (isEmpty(launch)) return;
  park(launch);
  try {
    window.history.replaceState(window.history.state, '', path);
  } catch {
    // Harmless - the params just stay visible.
  }
};

/** Park an intent that arrived by service-worker message. */
export const parkPushLaunch = (launch: PushLaunch): void => {
  if (!isEmpty(launch)) park(launch);
};

/** Take (and clear) the parked intent, if any and still fresh. */
export const consumePushLaunch = (): PushLaunch | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as PushLaunch & { at?: number };
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > MAX_AGE_MS) return null;
    const launch: PushLaunch = {};
    if (typeof parsed.readNotificationId === 'string')
      launch.readNotificationId = parsed.readNotificationId;
    if (typeof parsed.conversationId === 'string') launch.conversationId = parsed.conversationId;
    if (typeof parsed.callId === 'string') launch.callId = parsed.callId;
    if (parsed.answerCall === true && launch.callId) launch.answerCall = true;
    return isEmpty(launch) ? null : launch;
  } catch {
    return null;
  }
};
